'use client';

import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    id: 'faq-001',
    q: 'How long does a typical website project take?',
    a: 'Project timelines vary based on scope and complexity. A standard 5-page business website takes 3–4 weeks. An e-commerce platform with custom features typically runs 8–12 weeks. Enterprise-level platforms with integrations can take 3–6 months. We provide a detailed timeline during the discovery phase.',
  },
  {
    id: 'faq-002',
    q: 'What information do you need to get started?',
    a: "We start with a discovery call to understand your goals, target audience, brand guidelines, and technical requirements. You'll need to provide your domain details, existing branding assets (logo, colors, fonts), content drafts, and any reference websites you admire. We'll guide you through everything.",
  },
  {
    id: 'faq-003',
    q: 'Do you offer ongoing maintenance and support?',
    a: 'Yes — all plans include a support period post-launch (3, 6, or 12 months depending on tier). After that, we offer monthly maintenance retainers starting at $299/month covering security updates, content changes, performance monitoring, and priority bug fixes.',
  },
  {
    id: 'faq-004',
    q: 'Will my website be mobile-responsive and fast?',
    a: "Absolutely. Every website we build is mobile-first and tested across 40+ device/browser combinations. We target Lighthouse scores of 95+ across all categories (Performance, Accessibility, Best Practices, SEO) before any project ships. We don't launch slow websites.",
  },
  {
    id: 'faq-005',
    q: 'Can you work with my existing brand guidelines?',
    a: "Yes — we work with provided brand guidelines, design systems, and existing assets. If you don't have brand guidelines yet, we can create them as part of our branding service. We also offer a design-only package if you have in-house developers.",
  },
  {
    id: 'faq-006',
    q: 'What happens if I need changes after launch?',
    a: 'During the support period, we handle bug fixes and minor adjustments at no extra charge. Larger changes or new features are scoped and quoted separately. We keep a living spec document throughout the project so scope changes are always transparent and agreed upon.',
  },
  {
    id: 'faq-007',
    q: 'Do you handle hosting and domain registration?',
    a: 'We can manage hosting setup and domain configuration on your behalf, or work with your existing hosting environment. We recommend Vercel for Next.js projects and AWS/DigitalOcean for larger platforms. You retain full ownership of all accounts and credentials.',
  },
  {
    id: 'faq-008',
    q: 'How do payments work for projects?',
    a: 'We use a milestone-based payment structure: 40% upfront to begin work, 30% at design approval, and 30% at final delivery before launch. For retainer/maintenance services, we bill monthly via invoice. We accept wire transfer, credit card, and major payment platforms.',
  },
];

export default function FAQSection() {
  const [openId, setOpenId] = useState<string | null>('faq-001');

  return (
    <section id="faq" className="py-24 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="text-center mb-14">
          <span className="section-label">FAQ</span>
          <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">
            Questions We Get{' '}
            <span className="text-gradient-cyan">All the Time</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-base">
            If your question isn&apos;t here, reach out — we&apos;re happy to answer anything.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {faqs?.map((faq) => {
            const isOpen = openId === faq?.id;
            return (
              <div
                key={faq?.id}
                className={`glass-card rounded-xl border transition-all duration-300 overflow-hidden ${
                  isOpen ? 'border-primary/40' : 'border-border/40 hover:border-border/60'
                }`}
              >
                <button
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                  onClick={() => setOpenId(isOpen ? null : faq?.id)}
                  aria-expanded={isOpen}
                >
                  <span className="font-display font-semibold text-sm text-foreground pr-4">{faq?.q}</span>
                  <ChevronDown
                    size={16}
                    className={`text-muted-foreground flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isOpen && (
                  <div className="px-6 pb-5 border-t border-border/30 pt-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">{faq?.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}